import { useEffect, useState } from 'react';
import { Button as ButtonRB } from 'react-bootstrap';
import {
  ChevronDownIcon,
  ChevronUpIcon,
} from '@pmi/dsm-react/dist/components/icons/fa/16';
import { Button } from '@pmi/dsm-react';
import { useSelector } from 'react-redux';
import Overlay from '../../shared/components/Overlay';
import { useNavigate } from 'react-router';
import styles from './orders.module.scss';
import { accountSelector } from '../../shared/store/shared/accountSlice';
import { useGetOrderHistoryListQuery } from './apiSlice';
import formatCurrency from '../../shared/utils/formatCurrency';
import sortDeals from '../../shared/utils/sortDeals';
import {
  OrderHistoryResponse,
  OrderItems,
  Refund,
  RefundItems,
} from '../../shared/models/OrderHistory';
import { getSelfServiceUrl } from '../../shared/env-vars';
import { toDayMonthYear } from '../../shared/helpers';
import usePermissions from '../../shared/hooks/usePermissions';
import PERMISSIONS from '../../shared/permissions';
import RenderHtml from '../../shared/components/RenderHtml';

export default function OrderHistory() {
  const navigate = useNavigate();
  const { hasAccess, isLoading: isAccessLoading } = usePermissions();
  const selfServiceUrl = getSelfServiceUrl();
  const [expandedItems, setExpandedItems] = useState([]);
  const { currentAccountId } = useSelector(accountSelector);
  const { data, isLoading, isFetching } = useGetOrderHistoryListQuery(
    {
      accountId: currentAccountId,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !currentAccountId,
    },
  );

  const [sortedAgreement, setSortedAgreement] = useState<
    OrderHistoryResponse[]
  >([]);

  const toggleDetails = (orderNumber: string) => {
    const temp = (prevExpandedOrders: string[]) => {
      if (prevExpandedOrders.includes(orderNumber)) {
        return prevExpandedOrders.filter((order) => order !== orderNumber);
      }
      return [...prevExpandedOrders, orderNumber];
    };
    setExpandedItems(temp(expandedItems));
  };

  useEffect(() => {
    if (!isLoading && !isFetching && data && data.length > 0) {
      setSortedAgreement(
        sortDeals(data, 'agreementNumber').filter(
          (d) => d.orderItems.length > 0,
        ),
      );
    }
  }, [isLoading, isFetching, data]);

  return (
    <>
      <div className="d-flex flex-column flex-sm-row w-100 mb-4">
        <div className="flex-grow-1 d-flex justify-content-sm-end justify-content-start">
          {!hasAccess({
            permissions: [PERMISSIONS.ManageElevatedAdmins],
          }) && (
            <Button
              data-testid="new-order-button"
              onClick={() =>
                window.open(`${selfServiceUrl}/${currentAccountId}`, '_self')
              }
              titleText="New Order"
              variant="primary"
            />
          )}
        </div>
      </div>
      <div className={`${styles.orderHistoryPageContainer}`}>
        <div className={`${styles.orderCtr}`}>
          {sortedAgreement?.map((order) => {
            const isExpandedItem = expandedItems.includes(order.orderNumber);
            return (
              <div className="px-0 px-md-4">
                <div
                  id={`order-history-item-${order.orderNumber}`}
                  className={`${styles.orderRow} py-3 py-md-4 px-3 px-md-0`}
                  key={order.orderNumber}
                >
                  <div className="d-block d-md-flex justify-content-between gy-4">
                    <div className={`${styles.flex1} mb-3 mb-md-0`}>
                      <div className={`text-nowrap ${styles.label}`}>
                        Order Placed
                      </div>
                      <div
                        id={`order-history-order-date-${order.orderNumber}`}
                        className={styles.value}
                      >
                        {order?.orderDate
                          ? new Date(order.orderDate).toLocaleDateString(
                              'en-GB',
                              {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric',
                              },
                            )
                          : ''}
                      </div>
                    </div>
                    <div className={`${styles.flex2} mb-2 mb-md-0`}>
                      <div className={`text-nowrap ${styles.label}`}>
                        Ordered By
                      </div>
                      <div
                        id={`order-history-order-ordered-by-${order.orderNumber}`}
                        className={styles.value}
                        data-exclude-lg
                      >
                        {order.email}
                      </div>
                    </div>
                    <div className={`${styles.flex1} mb-2 mb-md-0`}>
                      <div className={styles.label}>Status</div>
                      <div
                        id={`order-history-order-status-${order.orderNumber}`}
                        className={styles.value}
                      >
                        {order.orderStatus}
                      </div>
                    </div>
                    <div className={`${styles.flex1} mb-2 mb-md-0`}>
                      <div className={styles.label}>Quantity</div>
                      <div
                        id={`order-history-order-quantity-${order.orderNumber}`}
                        className={styles.value}
                      >
                        {order.totalItems}
                      </div>
                    </div>
                    <div className={`${styles.flex1} mb-2 mb-md-0`}>
                      <div className={styles.label}>Total</div>
                      <div
                        id={`order-history-order-total-${order.orderNumber}`}
                        className={styles.value}
                      >
                        ${formatCurrency(order.grandTotal)}
                      </div>
                    </div>
                    <div>
                      <ButtonRB
                        className={`${styles.filterButton}`}
                        id={`order-history-order-link-${order.orderNumber}`}
                        onClick={() => {
                          toggleDetails(order.orderNumber);
                        }}
                        variant="link"
                      >
                        <div
                          id={`order-history-order-details-toggle-${order.orderNumber}`}
                          className={styles.toggleLabel}
                        >{`${isExpandedItem ? 'Hide' : 'See'} Details `}</div>
                        {isExpandedItem ? (
                          <ChevronUpIcon
                            className={`ms-1 ${styles.toggleIcon}`}
                            fontSize="sm"
                          />
                        ) : (
                          <ChevronDownIcon
                            className={`ms-1 ${styles.toggleIcon}`}
                            fontSize="sm"
                          />
                        )}
                      </ButtonRB>
                    </div>
                  </div>
                  {/** EXPANDED SECTION */}
                  {expandedItems.includes(order.orderNumber) && (
                    <>
                      {/** ORDER SECTION */}
                      <div>
                        {/** DESKTOP ORDER SECTION */}
                        <div className="d-none d-md-block">
                          {order.orderItems.length > 0 ? (
                            <div>
                              <div
                                className={`${styles.customVerticalBorder} mt-4 row mx-0 py-3 gy-2`}
                              >
                                <div className="col-6 p-0">
                                  <div
                                    className={`${styles.value} fw-semibold`}
                                  >
                                    Order Summary
                                  </div>
                                </div>
                                <div className="col-6 p-0" />
                                <div className="col-6 p-0">
                                  <div>
                                    <span className={`${styles.label} me2`}>
                                      Order #
                                    </span>
                                    <span
                                      id={`order-history-order-number-${order.orderNumber}`}
                                      className={styles.label}
                                      style={{
                                        fontWeight: 'normal',
                                      }}
                                    >
                                      {order.orderNumber}
                                    </span>
                                  </div>
                                </div>
                                <div
                                  className={`col-2 p-0 fw-bold text-end ${styles.label}`}
                                >
                                  Quantity
                                </div>
                                <div
                                  className={`col-2 p-0 fw-bold  text-end ${styles.label}`}
                                >
                                  Price
                                </div>
                                <div
                                  className={`col-2 p-0 fw-bold text-end ${styles.label}`}
                                >
                                  Total
                                </div>
                              </div>

                              {order.orderItems?.map((item: OrderItems, i) => (
                                <div
                                  className={`${
                                    order.orderItems.length - 1 !== i
                                      ? styles.customBottomBorder
                                      : ''
                                  } row mx-0 py-3`}
                                  key={item.sku}
                                >
                                  <div className="col-6 p-0">
                                    <RenderHtml
                                      tag="div"
                                      id={`order-history-order-item-title-${item.sku}`}
                                      className={`${styles.value}`}
                                      style={{
                                        fontSize: '14px',
                                      }}
                                      html={item.title}
                                    />
                                    <div className="py-2 p-0">
                                      <span
                                        id={`order-history-order-item-language-${item.sku}`}
                                        className={styles.language}
                                      >
                                        {item.language}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    id={`order-history-order-item-quantity-${item.sku}`}
                                    className={`col-2 p-0 text-end ${styles.value}`}
                                  >
                                    {item.quantity}
                                  </div>
                                  <div
                                    id={`order-history-order-item-price-${item.sku}`}
                                    className={`col-2 p-0 text-end ${styles.value}`}
                                  >
                                    ${formatCurrency(item.price)}
                                  </div>
                                  <div
                                    id={`order-history-order-item-subtotal-${item.sku}`}
                                    className={`col-2 p-0 text-end ${styles.value} `}
                                  >
                                    ${formatCurrency(item.subTotal)}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="d-flex justify-content-center py-4">
                              <p className={styles.tableValue}>
                                No Order Items Available
                              </p>
                            </div>
                          )}
                          <div className={`mb-3 ${styles.divider}`} />
                          <div className="mb-3">
                            <div className="row mx-0">
                              <div className="col-6 p-0" />
                              <div className="col-2 p-0" />
                              <div
                                className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                style={{
                                  fontSize: '14px',
                                }}
                              >
                                <div>Order Subtotal</div>
                                <div>Sales Tax</div>
                                <div className="fw-bold">Order Total</div>
                              </div>
                              <div
                                className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                style={{
                                  fontSize: '14px',
                                }}
                              >
                                <div
                                  id={`order-history-order-subtotal-${order.orderNumber}`}
                                >
                                  ${formatCurrency(order.subtotal)}
                                </div>
                                <div
                                  id={`order-history-order-tax-${order.orderNumber}`}
                                >
                                  ${formatCurrency(order.orderTaxes)}
                                </div>
                                <div
                                  id={`order-history-order-total-${order.orderNumber}`}
                                  className="fw-bold"
                                >
                                  ${formatCurrency(order.grandTotal)}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="d-block d-md-none">
                          {order.orderItems.length > 0 && (
                            <div
                              className={`${styles.customBottomBorder} mb-2 py-3`}
                            >
                              <div className={`${styles.value} fw-semibold`}>
                                Order Summary
                              </div>
                              <div>
                                <span className={`${styles.label} me-2`}>
                                  Order #
                                </span>
                                <span
                                  id={`order-history-mobile-order-number-${order.orderNumber}`}
                                  className={styles.label}
                                  style={{
                                    fontWeight: 'normal',
                                  }}
                                >
                                  {order.orderNumber}
                                </span>
                              </div>
                            </div>
                          )}
                          {order.orderItems?.map((item: OrderItems) => (
                            <>
                              <div>
                                <RenderHtml
                                  tag="div"
                                  className={`${styles.value}`}
                                  style={{
                                    fontSize: '14px',
                                  }}
                                  id={`order-history-mobile-order-item-title-${item.sku}`}
                                  html={item.title}
                                />
                                <div className="py-2 p-0">
                                  <span
                                    id={`order-history-mobile-order-item-language-${item.sku}`}
                                    className={styles.language}
                                  >
                                    {item.language}
                                  </span>
                                </div>
                                <div className="mb-2 mb-md-0">
                                  <div className={`${styles.label} fw-bold`}>
                                    Quantity
                                  </div>
                                  <div
                                    id={`order-history-mobile-order-item-quantity-${item.sku}`}
                                    className={styles.value}
                                  >
                                    {item.quantity}
                                  </div>
                                </div>
                                <div className="mb-2 mb-md-0">
                                  <div className={`${styles.label} fw-bold`}>
                                    Price
                                  </div>
                                  <div
                                    id={`order-history-mobile-order-item-price-${item.sku}`}
                                    className={styles.value}
                                  >
                                    ${formatCurrency(item.price)}
                                  </div>
                                </div>
                                <div className="mb-2 mb-md-0">
                                  <div className={`${styles.label} fw-bold`}>
                                    Total
                                  </div>
                                  <div
                                    id={`order-history-mobile-order-item-subtotal-${item.sku}`}
                                    className={styles.value}
                                  >
                                    ${formatCurrency(item.subTotal)}
                                  </div>
                                </div>
                              </div>
                              {item !==
                                order.orderItems[
                                  order.orderItems.length - 1
                                ] && (
                                <div
                                  className={`my-2 ${styles.customBottomBorder}`}
                                />
                              )}
                            </>
                          ))}
                          <div className={`${styles.divider} mb-3 mb-md-4`} />
                          <div>
                            <div
                              className={styles.value}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              Order Subtotal
                            </div>
                            <div
                              id={`order-history-mobile-order-subtotal-${order.orderNumber}`}
                              className={`${styles.value} mb-2`}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              ${formatCurrency(order.subtotal)}
                            </div>
                            <div
                              className={styles.value}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              Sales Tax
                            </div>
                            <div
                              id={`order-history-mobile-order-tax-${order.orderNumber}`}
                              className={`${styles.value} mb-2`}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              ${formatCurrency(order.orderTaxes)}
                            </div>
                            <div
                              className={`${styles.value} fw-bold`}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              Order Total
                            </div>
                            <div
                              id={`order-history-mobile-order-total-${order.orderNumber}`}
                              className={`${styles.value} fw-bold mb-2`}
                              style={{
                                fontSize: 14,
                              }}
                            >
                              ${formatCurrency(order.grandTotal)}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/** REFUND SECTION */}
                      {order.refunds.length > 0 && (
                        <>
                          {order.refunds.map((refund: Refund) => (
                            <div
                              style={{ backgroundColor: '#FBFBFB' }}
                              key={refund.refundIncrementId}
                            >
                              {/** DESKTOP REFUND SECTION */}
                              <div className="d-none d-md-block">
                                <div className="row mx-0 py-3 gy-2">
                                  <div className="col-6 p-0">
                                    <div
                                      className={`${styles.value} fw-semibold`}
                                    >
                                      Refund Summary
                                    </div>
                                  </div>
                                  <div className="col-6 p-0" />
                                  <div className="col-6 p-0">
                                    <div>
                                      <span className={`${styles.label} me-2`}>
                                        Refund Date
                                      </span>
                                      <span
                                        className={styles.label}
                                        style={{ fontWeight: 'normal' }}
                                      >
                                        {toDayMonthYear(refund.refundDate)}
                                      </span>
                                    </div>
                                  </div>
                                  <div
                                    className={`col-2 p-0 fw-bold text-end ${styles.label}`}
                                  >
                                    Quantity
                                  </div>
                                  <div
                                    className={`col-2 p-0 fw-bold  text-end ${styles.label}`}
                                  >
                                    Price
                                  </div>
                                  <div
                                    className={`col-2 p-0 fw-bold text-end ${styles.label}`}
                                  >
                                    Total
                                  </div>
                                </div>

                                {refund.refundItems?.map(
                                  (item: RefundItems, i) => (
                                    <div
                                      className={`${
                                        order.orderItems.length - 1 !== i
                                          ? styles.customBottomBorder
                                          : ''
                                      } row mx-0 py-3`}
                                      key={item.sku}
                                    >
                                      <div className="col-6 p-0">
                                        <RenderHtml
                                          tag="div"
                                          id={`order-history-order-item-title-${item.sku}`}
                                          className={`${styles.value}`}
                                          html={item.title}
                                        />
                                        <div className="py-2 p-0">
                                          <span
                                            id={`order-history-order-item-language-${item.sku}`}
                                            className={styles.language}
                                          >
                                            {item.language}
                                          </span>
                                        </div>
                                      </div>
                                      <div
                                        id={`order-history-order-item-quantity-${item.sku}`}
                                        className={`col-2 p-0 text-end ${styles.value}`}
                                      >
                                        -{item.quantity}
                                      </div>
                                      <div
                                        id={`order-history-order-item-price-${item.sku}`}
                                        className={`col-2 p-0 text-end ${styles.value}`}
                                      >
                                        ${formatCurrency(item.price)}
                                      </div>
                                      <div
                                        id={`order-history-order-item-subtotal-${item.sku}`}
                                        className={`col-2 p-0 text-end ${styles.value} `}
                                      >
                                        -${formatCurrency(item.subtotal)}
                                      </div>
                                    </div>
                                  ),
                                )}
                                <div className={`mb-3 ${styles.divider}`} />
                                <div className="mb-3">
                                  <div className="row mx-0">
                                    <div className="col-6 p-0" />
                                    <div className="col-2 p-0" />
                                    <div
                                      className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                      style={{ fontSize: '14px' }}
                                    >
                                      <div>Refund Subtotal</div>
                                      <div>Refund Tax</div>
                                      <div className="fw-bold">
                                        Refund Total
                                      </div>
                                    </div>
                                    <div
                                      className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                      style={{ fontSize: '14px' }}
                                    >
                                      <div
                                        id={`order-history-refund-subtotal-${order.orderNumber}`}
                                      >
                                        -${formatCurrency(refund.subtotal)}
                                      </div>
                                      <div
                                        id={`order-history-refund-tax-${order.orderNumber}`}
                                      >
                                        -${formatCurrency(refund.taxTotal)}
                                      </div>
                                      <div
                                        id={`order-history-refund-total-${order.orderNumber}`}
                                        className="fw-bold"
                                      >
                                        -${formatCurrency(refund.grandTotal)}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/** MOBILE REFUND SECTION */}
                              <div className="d-block d-md-none">
                                <div
                                  className={`${styles.customBottomBorder} mb-2 py-3`}
                                >
                                  <div
                                    className={`${styles.value} fw-semibold`}
                                  >
                                    Refund Summary
                                  </div>
                                  <div>
                                    <span
                                      className={`${styles.label} me-2 mb-2`}
                                    >
                                      Refund Date
                                    </span>
                                    <span
                                      className={styles.label}
                                      style={{ fontWeight: 'normal' }}
                                    >
                                      {toDayMonthYear(refund.refundDate)}
                                    </span>
                                  </div>
                                </div>
                                {refund.refundItems?.map(
                                  (item: RefundItems) => (
                                    <>
                                      <div>
                                        <RenderHtml
                                          tag="div"
                                          className={`${styles.value}`}
                                          style={{ fontSize: '14px' }}
                                          id={`order-history-mobile-order-item-title-${item.sku}`}
                                          html={item.title}
                                        />
                                        <div className="py-2 p-0">
                                          <span
                                            id={`order-history-mobile-order-item-language-${item.sku}`}
                                            className={styles.language}
                                          >
                                            {item.language}
                                          </span>
                                        </div>
                                        <div className="mb-2 mb-md-0">
                                          <div
                                            className={`${styles.label} fw-bold`}
                                          >
                                            Quantity
                                          </div>
                                          <div
                                            id={`order-history-mobile-order-item-quantity-${item.sku}`}
                                            className={styles.value}
                                          >
                                            -{item.quantity}
                                          </div>
                                        </div>
                                        <div className="mb-2 mb-md-0">
                                          <div
                                            className={`${styles.label} fw-bold`}
                                          >
                                            Price
                                          </div>
                                          <div
                                            id={`order-history-mobile-order-item-price-${item.sku}`}
                                            className={styles.value}
                                          >
                                            ${formatCurrency(item.price)}
                                          </div>
                                        </div>
                                        <div className="mb-2 mb-md-0">
                                          <div
                                            className={`${styles.label} fw-bold`}
                                          >
                                            Total
                                          </div>
                                          <div
                                            id={`order-history-mobile-order-item-subtotal-${item.sku}`}
                                            className={styles.value}
                                          >
                                            -${formatCurrency(item.subtotal)}
                                          </div>
                                        </div>
                                      </div>
                                      {item !==
                                        refund.refundItems[
                                          refund.refundItems.length - 1
                                        ] && (
                                        <div
                                          className={`my-2 ${styles.customBottomBorder}`}
                                        />
                                      )}
                                    </>
                                  ),
                                )}
                                <div
                                  className={`${styles.divider} mb-3 mb-md-4`}
                                />
                                <div>
                                  <div
                                    className={styles.value}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    Refund Subtotal
                                  </div>
                                  <div
                                    id={`order-history-mobile-refund-subtotal-${refund.refundIncrementId}`}
                                    className={`${styles.value} mb-2`}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    -${formatCurrency(refund.subtotal)}
                                  </div>
                                  <div
                                    className={styles.value}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    Refund Tax
                                  </div>
                                  <div
                                    id={`order-history-mobile-refund-tax-${refund.refundIncrementId}`}
                                    className={`${styles.value} mb-2`}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    -${formatCurrency(refund.taxTotal)}
                                  </div>
                                  <div
                                    className={`${styles.value} fw-bold`}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    Refund Total
                                  </div>
                                  <div
                                    id={`order-history-mobile-refund-total-${refund.refundIncrementId}`}
                                    className={`${styles.value} fw-bold mb-2`}
                                    style={{
                                      fontSize: 14,
                                    }}
                                  >
                                    -$
                                    {formatCurrency(refund.grandTotal)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/** GRAND TOTAL - Desktop & Mobile */}
                          <div className="d-none d-md-block">
                            <div className="row mx-0 my-3">
                              <div className="col-6 p-0" />
                              <div className="col-2 p-0" />
                              <div
                                className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                style={{ fontSize: '14px' }}
                              >
                                <div className={`${styles.value} fw-bold`}>
                                  Grand Total
                                </div>
                              </div>
                              <div
                                className={`col-2 p-0 ${styles.value} text-end text-nowrap`}
                                style={{ fontSize: '14px' }}
                              >
                                <div
                                  id={`order-history-order-total-${order.orderNumber}`}
                                  className={`${styles.value} fw-bold`}
                                >
                                  ${formatCurrency(order.grandTotal)}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="d-block d-md-none my-3">
                            <div className={`${styles.value} fw-bold`}>
                              Grand Total
                            </div>
                            <div
                              id={`order-history-order-total-${order.orderNumber}`}
                              className={`${styles.value} fw-bold`}
                            >
                              ${formatCurrency(order.grandTotal)}
                            </div>
                          </div>
                        </>
                      )}

                      {/** PAYMENT METHOD SECTION */}
                      <div className="px-0 px-md-2">
                        <div className="d-flex flex-column flex-md-row justify-content-between gx-2">
                          <div className="order-2 order-md-1 d-flex flex-nowrap align-items-center gap-2">
                            <Button
                              data-testid={`order-history-order-access-link-${order.orderNumber}`}
                              className="text-nowrap"
                              onClick={() =>
                                navigate(
                                  `/admin/${order.accountId}/${order.agreementNumber}`,
                                )
                              }
                              variant="primary"
                              size="xs"
                              enforceTitleCase={false}
                              titleText="Access Products"
                            />
                            <Button
                              data-testid={`order-history-order-confirmation-link-${order.orderNumber}`}
                              className="text-nowrap"
                              onClick={() =>
                                navigate(
                                  `/admin/${order.accountId}/order-history/confirmation/${order.orderId}`,
                                )
                              }
                              variant="outline-secondary"
                              size="xs"
                              enforceTitleCase={false}
                              titleText="Order Confirmation Page"
                            />
                          </div>
                          <div className="order-1 order-md-2">
                            <div className="d-flex flex-wrap align-items-center justify-content-md-end gx-2 mb-3 mb-md-0">
                              <div
                                className={styles.label}
                                style={{
                                  fontWeight: 'normal',
                                  fontSize: '14px',
                                }}
                              >
                                Payment Method
                              </div>
                              <div
                                id={`order-history-order-details-payment-method-type-${order.orderNumber}`}
                              >
                                {order.creditCardType ? (
                                  <div className="d-flex gap-2">
                                    <div>
                                      <img
                                        alt=""
                                        src={`/payment-icons/icon/${order?.creditCardType}.svg`}
                                      />
                                    </div>
                                    <div
                                      className={`${styles.value}`}
                                      style={{
                                        fontSize: '14px',
                                      }}
                                    >
                                      {order.creditCardType} ending in{' '}
                                      {order.creditCardLastFourDigit}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="d-flex gx-2">
                                    <div>
                                      <img
                                        alt=""
                                        src={`/payment-icons/icon/${order?.paymentMethod}.svg`}
                                      />
                                    </div>
                                    <div
                                      id={`order-history-order-details-payment-method-${order.orderNumber}`}
                                      className={`${styles.value}`}
                                      style={{
                                        fontSize: '14px',
                                      }}
                                    >
                                      {order.paymentMethod}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={isLoading || isFetching || isAccessLoading}
      />
    </>
  );
}
